import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, map, merge, Observable, ReplaySubject, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { Member } from 'app/models/organization/team/team';
import { TeamUser } from 'app/models/user/user';

@Component({
  selector: 'tm-autocomplete-select-members',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatChipsModule, MatAutocompleteModule, MatButtonModule],
  templateUrl: './autocomplete-select-members.component.html',
  styleUrls: ['./autocomplete-select-members.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true })
    }
  ]
})
export class AutocompleteSelectMembersComponent implements OnDestroy, OnInit {
  @Input() label = 'Select Members';
  @Input() controlName = 'members';
  private readonly membersList$ = new ReplaySubject<Array<TeamUser>>(1);
  private readonly destroy$ = new Subject<void>();
  @Input('members') set membersList(members: Array<TeamUser>) {
    if (Array.isArray(members)) {
      this.membersList$.next(members);
    }
  }
  @ViewChild('membersSearchInput') membersSearchInput: ElementRef<HTMLInputElement>;
  readonly membersSearchFC = new FormControl<string>('');
  readonly parentContainer = inject(ControlContainer);

  @Output() private readonly init = new EventEmitter<void>();

  get parentForm() {
    return this.parentContainer.control as FormGroup;
  }
  get membersFC() {
    return this.parentForm?.get(this.controlName);
  }

  members$: Observable<Array<TeamUser>>;


  selectMember(e: MatAutocompleteSelectedEvent) {
    const member = e.option.value;
    if (member) {
      this.membersFC.setValue([...this.membersFC.value, member]);
      this.membersSearchInput.nativeElement.value = '';
      this.membersSearchFC.setValue(null, { emitEvent: false });
    }
  }

  removeMember(id: string) {
    const members = (this.membersFC.value as Array<Member>).filter(member => member.id !== id);
    this.membersFC.setValue(members);
  }

  ngOnInit(): void {
    this.parentForm.addControl(this.controlName, new FormControl<Array<Member>>([]));
    this.init.emit();

    this.members$ = merge(this.membersFC?.valueChanges, this.membersSearchFC.valueChanges).pipe(
      takeUntil(this.destroy$),
      startWith([]),
      debounceTime(150),
      switchMap(() => {
        const membersList = this.membersFC.value || [];
        const membersSearch = this.membersSearchFC.value || '';
        return this.membersList$.pipe(
          map(members =>
            members.filter(
              (member) => member.displayName.toLowerCase().includes(membersSearch?.toLowerCase()?.trim()) && (!membersList.map(user => user.id).includes(member.id))
            )
          )
        )
      })
    );
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl(this.controlName);
    this.destroy$.next();
    this.destroy$.complete();
  }
}